import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Loader2, Dumbbell, Check, Mic, Volume2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Circuit exercises pool (self-contained manager). During Circuit-mode rounds the runner
 * announces a random ACTIVE exercise from this list during each timed break, in the George
 * voice. Exercises are time-based, so each only needs a name + active toggle + generated audio.
 * Rendered both on its own page (RoninExercises) and embedded in the Cue Library so everything
 * is adjustable in one area.
 */
export default function CircuitExercisesPanel() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [regenAll, setRegenAll] = useState(false);
  const [regenId, setRegenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ronin-box/exercises");
      setExercises(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const { data } = await api.post("/admin/ronin-box/exercises", { name });
      setExercises((arr) => [...arr, data]);
      setNewName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    } finally { setAdding(false); }
  };

  const toggle = async (ex) => {
    try {
      await api.patch(`/admin/ronin-box/exercises/${ex.id}`, { active: !ex.active });
      setExercises((arr) => arr.map((e) => (e.id === ex.id ? { ...e, active: !ex.active } : e)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Toggle failed");
    }
  };

  const saveEdit = async (ex) => {
    const name = editName.trim();
    if (!name) { setEditId(null); return; }
    try {
      const { data } = await api.patch(`/admin/ronin-box/exercises/${ex.id}`, { name });
      setExercises((arr) => arr.map((e) => (e.id === ex.id ? data : e)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rename failed");
    } finally { setEditId(null); }
  };

  const remove = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"?`)) return;
    try {
      await api.delete(`/admin/ronin-box/exercises/${ex.id}`);
      setExercises((arr) => arr.filter((e) => e.id !== ex.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const regenerateOne = async (ex) => {
    setRegenId(ex.id);
    try {
      const { data } = await api.post(`/admin/ronin-box/exercises/${ex.id}/regenerate-audio`);
      setExercises((arr) => arr.map((e) => (e.id === ex.id ? data : e)));
      toast[data.audio_status === "ready" ? "success" : "error"](
        data.audio_status === "ready" ? `Voice ready for "${data.name}"` : "Voice generation failed"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally { setRegenId(null); }
  };

  const generateAllVoices = async () => {
    setRegenAll(true);
    try {
      const { data } = await api.post("/admin/ronin-box/exercises/regenerate-all?missing_only=true");
      toast.success(`Generated ${data.regenerated} voice(s)${data.failed ? `, ${data.failed} failed` : ""}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally { setRegenAll(false); }
  };

  const activeCount = exercises.filter((e) => e.active).length;
  const missingVoice = exercises.filter((e) => e.audio_status !== "ready").length;

  return (
    <div data-testid="circuit-exercises-panel">
      <p className="text-sm text-zinc-400 mb-5">
        Announced during <span className="text-zinc-200 font-bold">Circuit mode</span> breaks in the <span className="text-zinc-200 font-bold">George</span> coach voice. Add any bodyweight movement — burpees, sit-ups, push-ups, etc. Only <span className="text-zinc-200 font-bold">active</span> exercises are used. <span className="text-amber-400">{activeCount} active.</span>
      </p>

      {missingVoice > 0 && (
        <div className="flex items-center justify-between gap-3 mb-5 border border-amber-700/50 bg-amber-500/10 rounded-md px-4 py-3" data-testid="exercise-voice-banner">
          <div className="flex items-center gap-2 text-amber-300 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{missingVoice} exercise(s) need the George voice generated.</span>
          </div>
          <Button onClick={generateAllVoices} disabled={regenAll} className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs shrink-0" data-testid="exercise-generate-all">
            {regenAll ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating…</> : <><Mic className="h-4 w-4 mr-1" /> Generate voices</>}
          </Button>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="e.g. Burpees"
          className="bg-[#0A0A0A] border-[#27272A] text-white rounded-sm"
          data-testid="exercise-new-input"
        />
        <Button onClick={add} disabled={adding || !newName.trim()} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs shrink-0" data-testid="exercise-add">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…</div>
      ) : exercises.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-[#27272A] rounded-md bg-[#0A0A0A]">
          <Dumbbell className="h-9 w-9 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">No exercises yet — add your first above.</p>
        </div>
      ) : (
        <div className="border border-[#27272A] rounded-md bg-[#0A0A0A] divide-y divide-[#27272A]" data-testid="exercises-list">
          {exercises.map((ex) => (
            <div key={ex.id} className="flex items-center gap-3 px-4 py-3" data-testid={`exercise-row-${ex.id}`}>
              <button onClick={() => toggle(ex)} className={`h-5 w-5 rounded-sm border flex items-center justify-center shrink-0 ${ex.active ? "bg-[#FF331F] border-[#FF331F]" : "border-[#3F3F46]"}`} data-testid={`exercise-toggle-${ex.id}`} title={ex.active ? "Active" : "Disabled"}>
                {ex.active && <Check className="h-3.5 w-3.5 text-white" />}
              </button>
              {editId === ex.id ? (
                <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(ex); if (e.key === "Escape") setEditId(null); }} onBlur={() => saveEdit(ex)} className="bg-[#050505] border-[#27272A] text-white rounded-sm h-8 flex-1" data-testid={`exercise-edit-input-${ex.id}`} />
              ) : (
                <button onClick={() => { setEditId(ex.id); setEditName(ex.name); }} className={`flex-1 text-left ${ex.active ? "text-white" : "text-zinc-500 line-through"}`} data-testid={`exercise-name-${ex.id}`}>
                  {ex.name}
                </button>
              )}
              <button
                onClick={() => regenerateOne(ex)}
                disabled={regenId === ex.id}
                className={`p-1.5 shrink-0 ${ex.audio_status === "ready" ? "text-emerald-400" : "text-amber-400"} hover:opacity-70`}
                data-testid={`exercise-regen-${ex.id}`}
                title={ex.audio_status === "ready" ? "George voice ready — click to regenerate" : "No voice yet — click to generate"}
              >
                {regenId === ex.id ? <Loader2 className="h-4 w-4 animate-spin" /> : ex.audio_status === "ready" ? <Volume2 className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <button onClick={() => remove(ex)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`exercise-delete-${ex.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
